// oxlint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore

import { format, parse } from 'footinch';

import type { types } from '@bedc/types';

// Define a simple console logger rather than importing from client/utils
// to avoid circular dependencies between shared/utils and client/utils
const log = {
	error: (context: string, message: string, ...args: unknown[]) => {
		console.error(`[${context}] ${message}`, ...args);
	},
};

export type ImperialMeasurementParts = {
	feet: string;
	inches: string;
	sixteenths: string;
};

export const getImperialPartsFromFeet = (feetValue: number): ImperialMeasurementParts => {
	const safeFeet = Number.isFinite(feetValue) ? Math.max(0, feetValue) : 0;
	const totalSixteenths = Math.round(safeFeet * 12 * 16);
	const feet = Math.trunc(totalSixteenths / (12 * 16));
	const remainder = totalSixteenths - feet * 12 * 16;
	const inches = Math.trunc(remainder / 16);
	const sixteenths = remainder - inches * 16;

	return {
		feet: String(feet),
		inches: String(inches),
		sixteenths: String(sixteenths),
	};
};

export const getImperialPartsFromInches = (value: number): ImperialMeasurementParts => {
	const nonNegative = Number.isFinite(value) ? Math.max(0, value) : 0;
	const totalSixteenths = Math.round(nonNegative * 16);
	const feet = Math.trunc(totalSixteenths / (12 * 16));
	const remainder = totalSixteenths - feet * 12 * 16;
	const inches = Math.trunc(remainder / 16);
	const sixteenths = remainder - inches * 16;

	return {
		feet: String(feet),
		inches: String(inches),
		sixteenths: String(sixteenths),
	};
};

/**
 * Calculates the scale value for converting pixel distance to real-world units.
 * @param pixelDistance - The distance in pixels.
 * @param referenceMeasurement - The real-world distance in a set of formatted strings (e.g., { ft: "5", in: "3" } or { mm: "1000" }).
 * @returns The scale factor to convert pixels to real-world units, or null if invalid inputs.
 */
export function computeScaleValue(
	pixelDistance: number,
	referenceMeasurement: { mm: string } | { ft?: string; in?: string }
): number | null {
	// Validate pixel distance to prevent division by zero
	if (!pixelDistance || isNaN(pixelDistance) || pixelDistance === 0) {
		log.error('Draw', 'Invalid pixel distance: must be a non-zero number');
		return null;
	}

	// Handle metric measurements
	if ('mm' in referenceMeasurement) {
		// Parse mm value to number
		const mmValue = parseFloat(referenceMeasurement.mm);

		// Validate the parsed value
		if (isNaN(mmValue)) {
			log.error('Draw', 'Invalid millimeter value:', referenceMeasurement.mm);
			return null;
		}

		// Calculate and return the scale value with fixed precision
		return Number((mmValue / pixelDistance).toFixed(10));
	}

	// Handle imperial measurements using footinch library
	try {
		// Check if we have any meaningful input
		if (!referenceMeasurement.ft && !referenceMeasurement.in) {
			log.error('Draw', 'Invalid imperial measurement: both feet and inches cannot be empty');
			return null;
		}

		// Format the imperial reference string properly for the footinch parser
		const imperialReferenceDistance = `${referenceMeasurement.ft ? referenceMeasurement.ft : 0} ${referenceMeasurement.in ?? 0}`;

		// Use the footinch parser to handle fractions properly
		const realDistance = parse.F(imperialReferenceDistance);

		// If parsing returns 0 or NaN, it's likely an invalid input
		if (!realDistance || isNaN(realDistance)) {
			log.error('Draw', 'Failed to parse imperial measurement:', imperialReferenceDistance);
			return null;
		}

		// Calculate scale value and limit floating point precision issues
		return Number((realDistance / pixelDistance).toFixed(10));
	} catch (error) {
		log.error('Draw', 'Error parsing imperial measurement:', error);
		return null;
	}
}

/**
 * Converts a pixel distance to a millimeter string representation.
 * @param meters - The distance in meters.
 * @param scaleValue - The scale factor to convert pixels to millimeters.
 * @returns The distance in millimeters as a string (with or without the 'mm' unit).
 */
function toMillimeterString(meters: number): string {
	/**
	 * Calculate the total millimeters by multiplying pixel distance and scale value,
	 * converting to millimeters, and rounding to two decimal places.
	 */
	const totalMillimeters = (1000.0 * meters).toFixed(2).toString().replace('.00', '');

	/** Return the total millimeters string without any units. */
	return totalMillimeters;
}

/**
 * Converts a pixel distance to a formatted string based on the scale and measurement system.
 * @param pixelDistance - The distance in pixels.
 * @param scaleValue - The scale factor for converting pixels to real-world units.
 * @param measurementSystem - The measurement system, either 'imperial' or 'metric'.
 * @returns The formatted distance string in the appropriate units.
 */
function getFormattedRealDistance(
	pixelDistance: number,
	scaleValue = 1,
	measurementSystem: types.ui.MeasurementSystem,
	imperialFractionDenominator = 8
): string {
	/** Convert the pixel distance to real-world distance using the scale value. */
	const realDistance = pixelDistance * scaleValue;

	const formatters: Record<string, () => string> = {
		imperial: () => {
			const formatted = format.FT.to.FT.IN.FRAC(imperialFractionDenominator)(realDistance);

			// Handle cases like "3' 0 1/4"" -> "3 1/4""
			// Check if we have feet and fractional inches with 0 whole inches
			const zeroInchesWithFraction = formatted.match(/(\d+)' 0 (\d+\/\d+)"/);
			if (zeroInchesWithFraction) {
				const [, feet, fraction] = zeroInchesWithFraction;
				return `${feet}' ${fraction}"`;
			}

			// Apply existing cleanup for other cases
			return formatted.replace(/^0'/, '').replace(/ 0"/, '');
		},
		metric: () => `${toMillimeterString(realDistance / 1000)} mm`,
	};

	if (!formatters[measurementSystem]) {
		throw new Error('Unsupported measurement system. Use "imperial" or "metric".');
	}

	return formatters[measurementSystem]();
}

/**
 * Converts a pixel distance to a formatted string based on the scale and measurement system.
 * @param pixelDistance - The distance in pixels.
 * @param scaleValue - The scale factor for converting pixels to real-world units.
 * @param measurementSystem - The measurement system, either 'imperial' or 'metric'.
 * @returns The formatted distance string in the appropriate units.
 */
function getFormattedRealDistanceNumber(
	pixelDistance: number,
	scaleValue = 1,
	measurementSystem: types.ui.MeasurementSystem
): number {
	/** Convert the pixel distance to real-world distance using the scale value. */
	const realDistance = pixelDistance * scaleValue;

	const formatters: Record<types.ui.MeasurementSystem, () => number> = {
		imperial: () => realDistance,
		metric: () => realDistance / 1000,
	};

	if (!formatters[measurementSystem]) {
		throw new Error('Unsupported measurement system. Use "imperial" or "metric".');
	}

	return formatters[measurementSystem]();
}

function getFormattedPitchValue(
	pitchValue: number,
	measurementSystem: types.ui.MeasurementSystem
): string {
	if (measurementSystem === 'imperial') {
		const rise = pitchValue;
		const run = 12;
		return `${rise}/${run}`;
	} else {
		// metric
		const rise = pitchValue;
		return `${rise}°`;
	}
}

export const conversionUtils = {
	getFormattedRealDistance,
	getFormattedRealDistanceNumber,
	computeScaleValue,
	toMillimeterString,
	getFormattedPitchValue,
	getImperialPartsFromFeet,
	getImperialPartsFromInches,
};
